
import React, { useRef, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Eye } from 'lucide-react';
import { CertificateTemplateElement } from '@/types/certificateTemplate';

interface PDFCertificatePreviewProps {
  pdfUrl: string;
  elements: CertificateTemplateElement[];
  certificateData: Record<string, string>;
  onDownload?: () => void;
}

const PDFCertificatePreview: React.FC<PDFCertificatePreviewProps> = ({
  pdfUrl,
  elements,
  certificateData,
  onDownload
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  
  // Update scale on container resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        // Adjust scale based on container width (assuming PDF is 800px wide)
        setScale(width / 800);
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Certificate Preview</CardTitle>
        {onDownload && (
          <Button onClick={onDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div ref={containerRef} className="relative border rounded overflow-hidden h-[500px]">
          {/* PDF Background */}
          <iframe
            src={`${pdfUrl}#toolbar=0`}
            className="w-full h-full"
            title="PDF Certificate Template"
          />
          
          {/* Dynamic Elements Overlay */}
          {elements.filter(el => el.dynamic && el.dataField).map(element => {
            const content = certificateData[element.dataField || ''] || `[${element.dataField}]`;
            const x = element.position.x - (parseInt(element.style?.width || '100') / 2);
            const y = element.position.y - (parseInt(element.style?.height || '30') / 2);
            
            return (
              <div 
                key={element.id}
                className="absolute flex items-center justify-center overflow-hidden"
                style={{
                  left: x * scale,
                  top: y * scale,
                  width: parseInt(element.style?.width || '100') * scale,
                  height: parseInt(element.style?.height || '30') * scale,
                  color: element.style?.color || '#333',
                  fontSize: parseInt(element.style?.fontSize || '16') * scale + 'px',
                  fontFamily: element.style?.fontFamily || 'serif',
                }}
              >
                {content}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PDFCertificatePreview;
